import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { API_URL } from '../lib/api';
import useRealtimeEvents from '../hooks/useRealtimeEvents';

const DuprControlContext = createContext(null);

export function DuprControlProvider({ children }) {
  const [duprRequired, setDuprRequiredState] = useState(true);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/site/dupr`);
      if (!res.ok) return;
      const data = await res.json();
      if (typeof data?.dupr_required === 'boolean') {
        setDuprRequiredState(data.dupr_required);
      }
    } catch {
      /* keep last known state */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useRealtimeEvents(['dupr_control_updated'], (msg) => {
    if (typeof msg?.dupr_required === 'boolean') {
      setDuprRequiredState(msg.dupr_required);
      setLoading(false);
    }
  });

  const setDuprRequired = useCallback(async (nextRequired) => {
    const token = localStorage.getItem('admin_token');
    const res = await fetch(`${API_URL}/admin/site/dupr`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ dupr_required: Boolean(nextRequired) }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const detail = typeof data.detail === 'string' ? data.detail : 'Could not update DUPR setting';
      throw new Error(detail);
    }
    if (typeof data.dupr_required === 'boolean') {
      setDuprRequiredState(data.dupr_required);
    }
    return data.dupr_required;
  }, []);

  const value = useMemo(
    () => ({ duprRequired, loading, refresh, setDuprRequired }),
    [duprRequired, loading, refresh, setDuprRequired],
  );

  return (
    <DuprControlContext.Provider value={value}>
      {children}
    </DuprControlContext.Provider>
  );
}

export function useDuprControl() {
  const ctx = useContext(DuprControlContext);
  if (!ctx) {
    return {
      duprRequired: true,
      loading: false,
      refresh: async () => {},
      setDuprRequired: async () => true,
    };
  }
  return ctx;
}
